import React, { useEffect, useRef, useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Image,
  Flex,
  Box,
  List,
  ListItem,
  IconButton
} from '@chakra-ui/react';
import { pxToRem } from "../../../utils/editor.ts";
import { OutlineNodeType, PaperOutlineNode } from "../../../types/Writer/OutlineNode.ts";
import { PaperService } from "../../../services/paperService.ts";
import { getTreeNodes } from "../../../types/Writer/NodesFunc.ts";
import { ClientError } from "../../../utils/clientError.ts";
import {
  ReorganizeSectionResultVersion,
  ReorganizeStructureRender
} from "../../../types/Writer/ReorganizeSectionResult.ts";
import { useTranslation } from "react-i18next";
import useOutlineStore from "../../../stores/modules/outline.ts";
import { cloneDeep } from "lodash";

interface ReorganizeProps {
  result: ReorganizeSectionResultVersion
  index: number
  currentLength: string
  currentDegree: string
  updateOutline: () => void
}

export const Reorganize: React.FC<ReorganizeProps> = (
  {
    result,
    currentDegree,
    currentLength,
    updateOutline
  }) => {
  const { t } = useTranslation()
  const { outline, currentSectionIndex, setOutline } = useOutlineStore()
  const [isReWrite, setIsReWrite] = useState<boolean>(false);
  const [isPopOpen, setIsPopOpen] = useState<boolean>(false);
  const [newSectionStructure, setNewSectionStructure] = useState<PaperOutlineNode>(null);
  const [structureTemp, setStructureTemp] = useState<ReorganizeStructureRender>({
    title: '',
    desc: '',
    nodeList: []
  });
  const [nodeListTemp, setNodeListTemp] = useState<PaperOutlineNode[]>([]);
  const [isReWriteLoading, setIsReWriteLoading] = useState<boolean>(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  // const [isAtBottom, setIsAtBottom] = useState(false);
  
  // 添加拖曳功能所需的状态
  const [position, setPosition] = useState({ x: 350, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // const handleScroll = () => {
    //   if (popoverRef.current) {
    //     const { scrollTop, scrollHeight, clientHeight } = popoverRef.current;
    //     const atBottom = scrollTop + clientHeight >= scrollHeight - 150;
    //     setIsAtBottom(atBottom);
    //   }
    // };
    // handleScroll()

    // const popoverElement = popoverRef.current;
    // if (popoverElement) {
    //   popoverElement.addEventListener('scroll', handleScroll);
    // }

    // return () => {
    //   if (popoverElement) {
    //     popoverElement.removeEventListener('scroll', handleScroll);
    //   }
    // };
  }, []);
  
  // 添加拖曳相关的事件处理函数
  const handleMouseDown = (e: React.MouseEvent) => {
    if (boxRef.current && e.target === boxRef.current.querySelector('header')) {
      setIsDragging(true);
      const rect = boxRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  /**
   * popup replace/rewrite button click
   */
  const replaceOrRewrite = async () => {
    if (isReWrite) {
      const newOutline = cloneDeep(outline)
      newOutline.nodes[currentSectionIndex] = newSectionStructure
      setOutline({ ...newOutline })
      setIsReWriteLoading(true)
      await updateOutline()
      setIsPopOpen(false)
      setIsReWriteLoading(false)
    } else {
      rewriteSectionReq()
    }
  }

 /**
  * call rewrite api
  */
  const rewriteSectionReq = async () => {
    setIsReWriteLoading(true)
    try {
      const result = await PaperService.rewriteSection(outline,
        structureTemp.structure, currentDegree, currentLength, currentSectionIndex);
      setNewSectionStructure(result.sectionNode)
      const list = getTreeNodes([result.sectionNode]);
      setNodeListTemp(list);
      setIsReWrite(true)
      setIsReWriteLoading(false)
    } catch (error) {
      setIsReWriteLoading(false)
      new ClientError(error).toast();
    }
  }

  /**
   * show reorganize modal, new structure
   */
  const showNewStructure = (reorganize: ReorganizeSectionResultVersion) => {
    setStructureTemp(
      {
        title: `Suggestion`,
        desc: reorganize.reorganize_strategy_and_advantage,
        nodeList: getTreeNodes([reorganize.reorganized_structure]),
        structure: reorganize.reorganized_structure
      }
    );
    setIsPopOpen(true)
  }

  return (
    <Flex width={pxToRem(248)}
          alignItems={"center"}
          justifyContent={"start"}>
      <VStack onClick={(e) => {
        e.stopPropagation()
        showNewStructure(result)
      }} cursor={"pointer"}>
        <Text
          width={pxToRem(248)}
          aria-multiline={true}
          fontSize={pxToRem(16)}
          fontWeight={700}
        >
          Suggestion
        </Text>
        <Text
          width={pxToRem(248)}
          aria-multiline={true}
          fontSize={pxToRem(14)}
          fontWeight={400}
        >
          {result.reorganize_strategy_and_advantage}
        </Text>
      </VStack>
      
      {isPopOpen && (
        <Box
          ref={boxRef}
          position={'fixed'}
          left={`${position.x}px`}
          top={`${position.y}px`}
          zIndex={1000}
          p={0}
          border={'2px solid #D3D7E2'}
          width={pxToRem(600)}
          bg={'white'}
          boxShadow={'0px 4px 12px rgba(0, 0, 0, 0.1)'}
          borderRadius={'md'}
          onMouseDown={handleMouseDown}
        >
          {isReWriteLoading && (
            <VStack
              width={pxToRem(600)}
              height={'100%'}
              position={'absolute'}
              justifyContent={'center'}
              background={'#FFFFFFBB'} 
              backdropFilter={'blur(6px)'}
            >
              <Image
                mt={4}
                w={pxToRem(60)}
                objectFit="cover"
                src="/static/loading-animate.svg"
                transform={"scale(2)"}
              />
              <Text color={'#8E9ABC'} fontSize={pxToRem(20)} mt={4} p={2}>{ t('editor-editormodel-reorganize-loading')}</Text>
              <IconButton
                bg="transparent"
                size={"xs"}
                mt={'1rem'}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsReWriteLoading(false);
                  setIsReWrite(false)
                }}
                icon={
                  <Image
                    w={pxToRem(32)}
                    objectFit="contain"
                    src={"/static/ToolAI/cancelCal.svg"}
                    alt="cancel"
                  />
                }
                aria-label="cancel"
              />
            </VStack>
          )}
          <Box 
            as="header" 
            borderBottom={'1px solid #E2E8F0'} 
            p={3} 
            display="flex" 
            justifyContent="space-between"
            alignItems="center"
            cursor="move"
          >
            <Image
              w={pxToRem(144)}
              h={"40px"}
              objectFit="contain"
              src={"/static/doenba-logo-blue.png"}
            />
            <IconButton
              size="sm"
              variant="ghost"
              onClick={() => setIsPopOpen(false)}
              aria-label="Close"
              icon={<Text margin={0} fontSize="lg">×</Text>}
            />
          </Box>
          <Box
            ref={popoverRef}
            maxH={pxToRem(400)}
            minH={pxToRem(100)}
            padding={pxToRem(32)}
            overflow={'auto'}
          >
            {!isReWrite ? (<VStack alignItems={"start"}>
                <Text fontSize={pxToRem(20)} fontWeight={"bold"} color={"#333333"}>
                  {structureTemp.title}
                </Text>
                <Text fontSize={pxToRem(14)} my={"10px"}>
                  {structureTemp.desc}
                </Text>
                <List style={{ paddingBottom: '80px' }}>
                  {structureTemp?.nodeList?.map((child, index) =>
                    (
                      <ListItem
                        flex={"column"}
                        key={index}
                        my={"10px"}
                      >
                        <HStack
                          alignItems={"flex-start"}
                          pl={(child.level !== 2 ? (28 * (child.level - 2)).toString() : 0) + "px"}
                        >
                          {/** reorganize 中会不会有equation table figure */}
                          {child.type === OutlineNodeType.Paragraph ? (
                            <VStack alignItems={"start"}>
                              <Text
                                color={"#333333"}
                                fontWeight={"bold"}
                                fontSize={pxToRem(18)}>{'Paragraph' + child.paragraph_index}</Text>
                              <List
                                styleType={"disc"}
                                pl={(child.level !== 2 ? (28 * (child.level - 2)).toString() : 0) + "px"}>
                                {child.key_points?.map((key_point, point_index) => (
                                  <ListItem key={point_index}>
                                    <Text color={"#444"}
                                          fontSize={pxToRem(14)}>KeyPoint {key_point}</Text>
                                  </ListItem>
                                ))}
                              </List>
                            </VStack>
                          ) : (
                            <Text
                              textColor={"333333"}
                              width={'100%'}
                              minHeight={pxToRem(20)}
                              fontSize={pxToRem(18)}
                              fontWeight={"bold"}
                            >
                              {child.name}
                            </Text>)}
                        </HStack>
                      </ListItem>))
                  }
                </List>
              </VStack>
            ) : (
              <List  style={{ paddingBottom: '80px' }}>
                {nodeListTemp?.map((child, index) =>
                  (
                    <ListItem
                      flex={"column"}
                      key={child.id?.toString() + child.name + index}
                    >
                      <HStack
                        alignItems={"flex-start"}
                        pl={(56 * (child.level - 1)).toString() + "px"}
                      >
                        {child.type == "paragraph" ? (
                          <Text
                            fontSize={pxToRem(16)}>{child.content}</Text>
                        ) : (
                          <Text
                            textColor={
                              child.level == 1 ? "#1D8AFF" : "#8E9ABC"
                            }
                            width={'100%'}
                            minHeight={pxToRem(20)}
                            fontSize={pxToRem(16)}
                            fontWeight={"bold"}
                          >
                            {child.name}
                          </Text>)}
                      </HStack>
                    </ListItem>))
                }
              </List>
            )}
            {<Box position={'absolute'} bottom={'60px'} left={0}
              width={"calc(100% - 2rem)"} height={'100px'} bg={'linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 1))'}></Box>}
          </Box>
          <Box textAlign={"right"} borderTop={'1px solid #E2E8F0'} p={3} h={'60px'}>
            <Button
              width={pxToRem(100)}
              height={pxToRem(34)}
              fontSize={'1rem'}
              isLoading={isReWriteLoading}
              textColor={"#FFFFFF"}
              border={"1px solid #1D8AFF"}
              bgColor={"#1D8AFF"}
              _hover={{ bg: '#1D8AFF' }}
              mt={2}
              onClick={(e) => {
                e.stopPropagation()
                replaceOrRewrite()
              }}
            >
              {isReWrite ? t("editor-editormodel-reorganize-replace") :
                t("editor-editormodel-reorganize-rewrite")}
            </Button>
          </Box>
        </Box>
      )}
    </Flex>
  )
};

export default Reorganize;